import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isSuspended: boolean;
  needsMFA: boolean;
  signUp: (email: string, password: string, userData: any) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  clearSuspension: () => void;
  clearMFARequirement: () => void;
  checkMFAStatus: () => Promise<{ enabled: boolean; factorCount: number }>;
  verifyTOTPCode: (code: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isSuspended: false,
  needsMFA: false,
  signUp: async () => ({ error: null }),
  signIn: async () => ({ error: null }),
  signOut: async () => {},
  resetPassword: async () => ({ error: null }),
  clearSuspension: () => {},
  clearMFARequirement: () => {},
  checkMFAStatus: async () => ({ enabled: false, factorCount: 0 }),
  verifyTOTPCode: async () => ({ success: false }),
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSuspended, setIsSuspended] = useState(false);
  const [needsMFA, setNeedsMFA] = useState(false);

  // Function to check user status and handle suspension (debounced)
  const checkUserStatus = async (userId: string) => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('status')
        .eq('id', userId)
        .maybeSingle();

      if (profile?.status === 'suspended') {
        console.log('User is suspended, forcing sign out');
        setIsSuspended(true);
        // Force immediate sign out
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error checking user status:', error);
      return true; // Allow access if there's an error to prevent lockout
    }
  };

  const clearSuspension = () => {
    setIsSuspended(false);
    // Clear any existing session data to force fresh login
    setUser(null);
    setSession(null);
  };

  const clearMFARequirement = () => {
    setNeedsMFA(false);
  };

  // Separate effect for periodic suspension checks to avoid stale closures
  useEffect(() => {
    if (!session?.user) return;
    
    const suspensionCheckInterval = setInterval(async () => {
      const canAccess = await checkUserStatus(session.user.id);
      if (!canAccess) {
        // The checkUserStatus function already handles sign out and state updates
      }
    }, 300000); // 5 minutes

    return () => {
      clearInterval(suspensionCheckInterval);
    };
  }, [session?.user?.id]); // Re-create interval when user changes

  useEffect(() => {
    let isAuthStateChangeActive = true;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isAuthStateChangeActive) return;

        // Clear states first to prevent any brief access
        if (isAuthStateChangeActive) {
          setSession(null);
          setUser(null);
        }

        if (session?.user) {
          // Set session immediately for responsiveness
          if (isAuthStateChangeActive) {
            setSession(session);
            setUser(session.user);
            setLoading(false);
          }

          // Defer suspension check to avoid blocking UI
          setTimeout(async () => {
            try {
              const canAccess = await checkUserStatus(session.user.id);
              if (!canAccess) {
                await supabase.auth.signOut();
                if (isAuthStateChangeActive) {
                  setIsSuspended(true);
                  setSession(null);
                  setUser(null);
                }
              }
            } catch (err) {
              console.error('Suspension check error:', err);
            }
          }, 0);

          // Update last_login asynchronously after setting session
          if (event === 'SIGNED_IN') {
            setTimeout(async () => {
              try {
                await supabase
                  .from('profiles')
                  .update({ last_login: new Date().toISOString() })
                  .eq('id', session.user.id);
              } catch (error) {
                console.error('Error updating last_login:', error);
              }
            }, 0);
          }
        } else {
          // No session - clear MFA requirement and suspension if we're not currently suspended
          if (isAuthStateChangeActive) {
            setNeedsMFA(false); // Clear MFA requirement when session ends
            setLoading(false);
          }
        }
      }
    );

    // THEN check for existing session (with robust error handling)
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!isAuthStateChangeActive) return;

        if (session?.user) {
          // Set session immediately for responsiveness
          if (isAuthStateChangeActive) {
            setSession(session);
            setUser(session.user);
          }

          // Defer suspension check and last_login update
          setTimeout(async () => {
            try {
              const canAccess = await checkUserStatus(session.user.id);
              if (!canAccess) {
                await supabase.auth.signOut();
                if (isAuthStateChangeActive) {
                  setIsSuspended(true);
                  setSession(null);
                  setUser(null);
                }
                return;
              }

              // Update last_login after validating access
              try {
                await supabase
                  .from('profiles')
                  .update({ last_login: new Date().toISOString() })
                  .eq('id', session.user.id);
              } catch (error) {
                console.error('Error updating last_login for existing session:', error);
              }
            } catch (err) {
              console.error('Error during initial suspension check:', err);
            } finally {
              if (isAuthStateChangeActive) {
                setLoading(false);
              }
            }
          }, 0);
        } else {
          if (isAuthStateChangeActive) {
            setSession(null);
            setUser(null);
            setNeedsMFA(false); // Clear MFA requirement when no session exists
            setLoading(false);
          }
        }
      } catch (err) {
        console.error('Error during initial session fetch:', err);
        if (isAuthStateChangeActive) {
          setSession(null);
          setUser(null);
          setLoading(false);
        }
      }
    })();

    return () => {
      isAuthStateChangeActive = false;
      subscription.unsubscribe();
    };
  }, []); // Remove dependencies to prevent infinite loops

  // Safety fallback: ensure loading doesn't hang indefinitely
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => setLoading(false), 5000);
    return () => clearTimeout(timer);
  }, [loading]);

  const signUp = async (email: string, password: string, userData: any) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: userData
      }
    });
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { error };
      }

      // If sign-in succeeded, check if user has MFA enabled and require verification
      if (data.user && data.session) {
        try {
          // Check if user has verified MFA factors
          const { data: factors } = await supabase.auth.mfa.listFactors();
          console.log('MFA factors check result:', factors);
          const verifiedFactors = factors?.totp?.filter(factor => factor.status === 'verified') || [];
          console.log('Verified factors found:', verifiedFactors.length);
          
          // If user has verified MFA factors, they need to complete MFA verification
          if (verifiedFactors.length > 0) {
            console.log('User has verified MFA factors, requiring MFA verification');
            // Don't sign out here - we need the session for MFA verification
            setNeedsMFA(true);
            return { error: { message: 'MFA_REQUIRED', mfaRequired: true } };
          }
        } catch (mfaError) {
          console.warn('MFA check failed:', mfaError);
          // If we can't check MFA status, assume no MFA and proceed normally
        }

        // Check if user is suspended after successful auth
        const { data: profile } = await supabase
          .from('profiles')
          .select('status')
          .eq('id', data.user.id)
          .maybeSingle();

        if (profile?.status === 'suspended') {
          // Force sign out immediately and set suspension state
          await supabase.auth.signOut();
          setIsSuspended(true);
          setUser(null);
          setSession(null);
          return { error: { message: 'Your account has been suspended. Please contact support at support@statconsult.org' } };
        }
      }

      // Clear MFA requirement on successful login (only if no MFA was required)
      setNeedsMFA(false);
      return { error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error };
    }
  };

  const signOut = async () => {
    setNeedsMFA(false); // Clear MFA requirement on logout
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const checkMFAStatus = async () => {
    try {
      const { data: factors, error } = await supabase.auth.mfa.listFactors();
      
      if (error) {
        if (error.message.includes('MFA is not enabled')) {
          return { enabled: false, factorCount: 0 };
        }
        throw error;
      }

      const verifiedFactors = factors?.totp?.filter(factor => factor.status === 'verified') || [];
      
      return {
        enabled: verifiedFactors.length > 0,
        factorCount: verifiedFactors.length
      };
    } catch (error) {
      console.error('Error checking MFA status:', error);
      return { enabled: false, factorCount: 0 };
    }
  };

  const verifyTOTPCode = async (code: string) => {
    try {
      // Get verified factors
      const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
      
      if (factorsError) throw factorsError;
      
      const verifiedFactor = factors?.totp?.find(factor => factor.status === 'verified');
      if (!verifiedFactor) {
        return { success: false, error: 'No verified MFA factor found' };
      }

      // Create challenge
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: verifiedFactor.id
      });

      if (challengeError) throw challengeError;

      // Verify code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: verifiedFactor.id,
        challengeId: challenge.id,
        code: code
      });

      if (verifyError) {
        return { success: false, error: verifyError.message };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error verifying TOTP code:', error);
      return { success: false, error: error.message || 'Verification failed' };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        isSuspended,
        needsMFA,
        signUp,
        signIn,
        signOut,
        resetPassword,
        clearSuspension,
        clearMFARequirement,
        checkMFAStatus,
        verifyTOTPCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};